import React, { Component } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { aspectRatio as ar } from "../../../../../common/parameters";

import player from "../../../../player";
import Icon from "../../../../icon";

import Draggable from "../../../Draggable";

import Wrapper from "../../Wrapper";

import getBirdsPath from "../Common/getBirdsPath";
import MessageDisplay from "../Common/MessageDisplay";
import NotEdited from "../Common/NotEdited";

import { SubmitButton, PlayBtn, CheckPlayBtn, Label } from "../common-style";

import Box from "./Box";
import { Drop, Drag, Playground, Asset1Wrapper, Asset2Wrapper } from "./styles";
import messages from "./messages";
import { getRawRhyt, randomizeOrder, isNearAnyDrop, getBoxWidth, getBoxWidths, parseScoreToBoxes } from "./utils";
import { IProps, IState } from "./interfaces";
import { IRhytSortingData } from "../../../../../common/types";
import getScore from "../Common/getScore";
import getTempo from "../Common/getTempo";

import WAAClock from "waaclock";

const margin = 20;
const padding = 30;

class RhytSorting extends Component<IProps, IState> {
    static defaultProps = {
        handlePlayCount: () => { },
        handleFailCount: () => { },
    };
    max_zIndex = 1;
    clock: any = undefined;
    buffers: AudioBufferSourceNode[] = [];
    timer: ReturnType<typeof setTimeout>;
    frame?: HTMLDivElement;
    originalDrags?: IState["drags"];
    constructor(props: IProps) {
        super(props);
        this.state = {
            drags: [],
            drops: [],
            zIndex: [],
            solutions: [],
            dropTarget: null,
            incorrectBars: null,
            randomOrder: [],
            frameScale: 1,
            playing: false,
            playingDrop: null,
            submitable: false,
            completed: false,
            checkPlaying: false,
            isBarPlay: false,
            widths: [],
            transition: false,
        };
        if (props.setReset) {
            props.setReset(this.resetTask);
        }
        if (props.saveState) {
            props.saveState(this.saveState);
        }
        if (props.restoreState) {
            props.restoreState(this.restoreState);
        }
        if (props.showSolution) {
            props.showSolution(this.showSolution);
        }
    }
    resetTask = () => {
        this.setPositions();
        this.setState({
            submitable: false,
            completed: false,
            incorrectBars: null,
        });
    };

    saveState = () => {
        return {
            drags: this.state.drags,
            zIndex: this.state.zIndex,
            solutions: this.state.solutions,
            incorrectBars: this.state.incorrectBars,
            submitable: this.state.submitable,
            completed: this.state.completed,
            randomOrder: this.state.randomOrder,
        };
    };
    restoreState = (state: IState) => {
        this.setState({
            drags: state.drags || [],
            zIndex: state.zIndex,
            solutions: state.solutions,
            incorrectBars: state.incorrectBars,
            submitable: state.submitable,
            completed: state.completed,
            randomOrder: state.randomOrder,
        });
    };
    showSolution = () => {
        let solutions: IState["solutions"] = [];
        const { drags, drops, randomOrder } = this.state;
        drops.forEach((drop, i) => {
            solutions[i] = i;
            drags[randomOrder.indexOf(i)].x = drop.x;
            drags[randomOrder.indexOf(i)].y = drop.y;
        });
        this.setState({
            solutions,
            drags,
            completed: true,
            incorrectBars: [],
        });
    };
    componentDidMount() {
        this.setPositions();
        this.forceUpdate();
        setTimeout(this.setScale);
        window.addEventListener("resize", this.setScale);
    }

    blur = () => {
        this.stop();
    };
    onPlayBar = (barIndex: number) => {
        if (this.state.playing || this.state.checkPlaying) return;
        this.play(false, true, barIndex);
    };
    play = async (checkPlay?: boolean, isBarPlay?: boolean, barPlayed?: number) => {
        const { playing, checkPlaying, solutions, randomOrder } = this.state;
        if (playing || checkPlaying) {
            return this.stop();
        }
        if (!player.slap) {
            const success = await player.loadSlap();
            if (!success) return this.props.showNotification("Error: Failed to load audio!", "warning");
        }
        window.addEventListener("blur", this.blur);
        const { task, plan } = this.props;
        let { boxes = this.getBoxesFromRefScore().boxes } = task.data as IRhytSortingData;
        let rhyt = getRawRhyt(boxes);
        let cT = 0;
        let delay = 0.1;
        let tempo = task.data.tempo || getTempo(plan, task);
        this.clock = new WAAClock(player.context, { toleranceEarly: 0.1 });
        this.clock.start();
        this.buffers = [];
        this.setState({
            checkPlaying: !!checkPlay,
            playing: !checkPlay,
            isBarPlay: !!isBarPlay
        });
        if (!checkPlay) {
            this.props.handlePlayCount!();
        }
        let offset = player.context.currentTime + delay;
        for (let a = 0; a < rhyt.length; a++) {
            if (isBarPlay && a !== randomOrder[barPlayed!]) {
                continue;
            }
            if (checkPlay) {
                for (let b = 0; b < rhyt[randomOrder[solutions[a]!]].length; b++) {
                    if (rhyt[randomOrder[solutions[a]!]][b] > 0) {
                        let volume = b === 0 ? "forte" : "mezzo";
                        let bufferNode = player.slap!.createNode(volume);
                        bufferNode.start(cT + offset);
                        this.buffers.push(bufferNode);
                        this.clock.callbackAtTime(() => {
                            if (b === 0) {
                                this.setState({ playingDrop: a });
                            }
                        }, cT + offset);
                    } else {
                        this.clock.callbackAtTime(() => {
                            if (b === 0) {
                                this.setState({ playingDrop: a });
                            }
                        }, cT + offset);
                    }
                    cT += (60 / tempo) * Math.abs(rhyt[randomOrder[solutions[a]!]][b]);
                }
            } else {
                for (let b = 0; b < rhyt[a].length; b++) {
                    if (rhyt[a][b] > 0) {
                        let volume = b === 0 ? "forte" : "mezzo";
                        let bufferNode = player.slap!.createNode(volume);
                        bufferNode.start(cT + player.context.currentTime + delay);
                        this.buffers.push(bufferNode);
                        this.clock.callbackAtTime(() => {
                            if (b === 0) {
                                this.setState({ playingDrop: a });
                            }
                        }, cT + offset);
                    } else {
                        this.clock.callbackAtTime(() => {
                            if (b === 0) {
                                this.setState({ playingDrop: a });
                            }
                        }, cT + offset);
                    }
                    cT += (60 / tempo) * Math.abs(rhyt[a][b]);
                }
            }
        }
        this.clock.callbackAtTime(() => {
            this.stop();
        }, cT + offset);
    };

    stop() {
        window.removeEventListener("blur", this.blur);
        this.buffers.forEach(buffer => {
            buffer.stop();
        });
        this.buffers = [];
        if (this.clock) {
            this.clock.stop();
            delete this.clock;
        }
        this.setState({ playing: false, playingDrop: null, checkPlaying: false, isBarPlay: false });
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.setScale);
        window.removeEventListener("blur", this.blur);
        clearTimeout(this.timer);
        if (this.state.playing) {
            this.stop();
        }
    }
    UNSAFE_componentWillReceiveProps() {
        if (!this.props.playPlan && !this.props.join) {
            this.setPositions();
        }
    }
    setScale = () => {
        clearTimeout(this.timer);
        if (this.props.edit) {
            this.setState({ submitable: false });
        }
        this.timer = setTimeout(() => {
            if (!this.frame?.children?.[0]) return;
            let frameWidth = this.frame.children[0].getBoundingClientRect().width;
            let frameScale = frameWidth / ar.width;
            this.setState({ frameScale });
        }, 550);
    };

    getBoxesFromRefScore = () => {
        const { plan, task } = this.props;
        const score = getScore(plan, task);
        const { bars, breaks } = parseScoreToBoxes(score);
        return { boxes: bars, breaks };
    }
    setPositions = () => {
        const { task } = this.props;
        if (!task.data) return;
        if (!task.data.boxes && !task.ref) return;
        let refBoxes: string[][] = [], refBreaks: number[] = [];
        if (task.ref) {
            ({ boxes: refBoxes, breaks: refBreaks } = this.getBoxesFromRefScore());
        }
        const { boxes = refBoxes } = task.data;

        const scale = task.data.scale || 1.2;
        const breaks = task.data.breaks || (task.ref ? refBreaks : []);
        let offsetX = 0;
        let offsetXdrops = 0;
        let maxX = 0;
        let maxXdrops = 0;
        const drags: IState["drags"] = [];
        const drops: IState["drops"] = [];
        const zIndex: IState["zIndex"] = [];
        let solutions: IState["solutions"] = [];
        let { randomOrder } = this.state;
        let valid = true;
        if (boxes.length === 1) {
            randomOrder = [0];
        } else if (randomOrder.length !== boxes.length) {
            randomOrder = randomizeOrder({ boxes });
        }
        let widths: IState["widths"] = [];
        // drags
        let x = margin * scale;
        let y = ar.height / 2 + margin * scale + padding;
        widths = getBoxWidths(boxes);
        boxes.forEach((box, index) => {
            box = boxes[randomOrder[index]];
            const width = widths[randomOrder[index]] * scale;
            index = randomOrder[index];
            // let width = getBoxWidth(box) * scale;
            if (x + width + margin * scale > ar.width) {
                x = margin * scale;
                y += (80 + margin) * scale;
                if (y + 80 * scale + margin * scale > ar.height) {
                    valid = false;
                }
            }
            drags.push({ x, y });
            zIndex.push(1);
            solutions.push(null);
            x += width + margin * scale;
            maxX = Math.max(x, maxX);
        });

        // drops
        x = margin * scale;
        y = margin * scale + padding * 2;
        boxes.forEach((box, index) => {
            let width = widths[index] * scale;
            if (
                x + width + margin * scale > ar.width ||
                (Array.isArray(breaks) && breaks.indexOf(index - 1) !== -1)
            ) {
                x = margin * scale;
                y += 100 * scale;
                if (y + 100 * scale + margin * scale > ar.height) {
                    valid = false;
                }
            }
            drops.push({ x, y, width });
            zIndex.push(1);
            x += width + margin * scale;
            maxXdrops = Math.max(x, maxXdrops);
        });
        offsetX = (ar.width - maxX) / 2;
        offsetXdrops = (ar.width - maxXdrops) / 2;
        drags.forEach(drag => {
            drag.x += offsetX;
        });
        drops.forEach(drop => {
            drop.x += offsetXdrops;
        });
        this.originalDrags = JSON.parse(JSON.stringify(drags));
        if (this.props.scaleAlert) {
            this.props.scaleAlert(!valid);
        }

        if (this.props.preview) {
            drags.forEach((drag, index) => {
                drag.x = drops[randomOrder[index]].x;
                drag.y = drops[randomOrder[index]].y;
            });
        }
        this.setState({ drags, zIndex, drops, solutions, randomOrder, submitable: false, widths });
    };

    draggedToDrop = ({ index, dropTarget }: { index: number, dropTarget: number }) => {
        const { solutions, drags, drops } = this.state;
        if (typeof solutions[dropTarget] === "number") {
            drags[solutions[dropTarget]!].y = this.originalDrags![solutions[dropTarget]!].y;
            drags[solutions[dropTarget]!].x = this.originalDrags![solutions[dropTarget]!].x;
        }
        solutions[dropTarget] = index;
        drags[index].x = drops[dropTarget].x;
        drags[index].y = drops[dropTarget].y;
        this.setState({ solutions, drags }, this.checkReady);
    };

    checkReady = () => {
        const { solutions } = this.state;
        if (solutions.filter(solution => solution === null).length === 0) {
            this.setState({ submitable: true });
        } else {
            this.setState({ submitable: false });
        }
    };

    clickDrag = (index: number) => {
        const { solutions, drags, drops } = this.state;
        if (solutions.indexOf(index) === -1) {
            let dropTarget = solutions.indexOf(null);
            solutions[dropTarget] = index;
            drags[index].x = drops[dropTarget].x;
            drags[index].y = drops[dropTarget].y;
        } else {
            solutions[solutions.indexOf(index)] = null;
            drags[index].x = this.originalDrags![index].x;
            drags[index].y = this.originalDrags![index].y;
        }
        this.setState({ solutions, drags, dropTarget: null, incorrectBars: null }, () => this.checkReady());
    };

    submit = () => {
        const { solutions, randomOrder } = this.state;
        const { task } = this.props;
        const { boxes = this.getBoxesFromRefScore().boxes } = task.data;
        let incorrectBars: number[] = [];
        solutions.forEach((solution, index) => {
            if (boxes[randomOrder[solution!]].join(",") !== boxes[index].join(",")) {
                incorrectBars.push(solution!);
            }
        });

        this.setState({ incorrectBars, completed: incorrectBars.length === 0 });
        if (incorrectBars.length === 0) {
            if (this.props.completed) {
                this.props.completed("rhytSorting");
            }
        } else {
            this.props.handleFailCount!();
        }
    };

    draggedFromDrop = (index: number) => {
        const { solutions } = this.state;
        solutions.forEach((solution, solutionIndex) => {
            if (solution === index) {
                solutions[solutionIndex] = null;
            }
        });
        this.setState({ solutions, incorrectBars: null });
    };

    render() {
        const { task, isLow, isDemo, handleHomeworkNext, isEaster, isChristmas } = this.props;
        const {
            drags,
            zIndex,
            drops,
            dropTarget,
            solutions,
            incorrectBars,
            transition,
            randomOrder,
            frameScale,
            playing,
            playingDrop,
            submitable,
            completed,
            checkPlaying,
            widths,
            isBarPlay,
        } = this.state;
        const boxes = task?.data?.boxes || (task.ref ? this.getBoxesFromRefScore().boxes : undefined);
        if (!task.data || !boxes || !boxes[0] || !boxes[0].length) {
            if (!task.ref) {
                return (
                    <div>
                        <Wrapper>
                            <NotEdited />
                        </Wrapper>
                    </div>
                );
            }
        }
        const data = task.data as IRhytSortingData;
        const scale = data.scale || 1.2;
        return (
            <div ref={e => { this.frame = e!; }}>
                {!!task.data && drags.length > 0 && (
                    <Wrapper isDemo={isDemo}>
                        {isLow && (
                            <>
                                <Asset1Wrapper>
                                    <img src={getBirdsPath("/rhytSorting/asset-01.svg")} alt="decoration" />
                                </Asset1Wrapper>
                                <Asset2Wrapper>
                                    <img src={getBirdsPath("/rhytSorting/asset-02.svg")} alt="decoration" />
                                </Asset2Wrapper>
                            </>
                        )}
                        <Playground isLow={isLow}>
                            {boxes.map((box, index) => {
                                index = randomOrder[index];
                                box = boxes[randomOrder[index]];
                                return (
                                    <Draggable
                                        key={index}
                                        transition={transition}
                                        position={{
                                            x: drags[index].x,
                                            y: drags[index].y,
                                        }}
                                        onDragStart={() => {
                                            if (solutions.indexOf(index) !== -1) {
                                                this.draggedFromDrop(index);
                                            }
                                            this.setState({ transition: false });
                                            this.checkReady();
                                        }}
                                        onDrag={(x: number, y: number) => {
                                            x = Math.min(
                                                x,
                                                ar.width - getBoxWidth(boxes[randomOrder[index]]) * scale
                                            );
                                            y = Math.min(y, ar.height - (100 - margin) * scale + (isLow ? 100 : 0));
                                            drags[index] = { x, y };
                                            let dropTarget = isNearAnyDrop(drops, x, y);
                                            this.setState({ drags, dropTarget });
                                        }}
                                        onStart={() => {
                                            this.max_zIndex++;
                                            zIndex[index] = this.max_zIndex;
                                            this.setState({ zIndex });
                                        }}
                                        onStop={() => {
                                            if (dropTarget !== null) {
                                                this.draggedToDrop({ index, dropTarget });
                                            } else {
                                                drags[index] = {
                                                    x: this.originalDrags![index].x,
                                                    y: this.originalDrags![index].y,
                                                };
                                            }
                                            this.setState({ dropTarget: null, transition: true, drags });
                                        }}
                                        onClick={() => {
                                            this.setState({ transition: true });
                                            this.clickDrag(index);
                                        }}
                                        scale={frameScale * (isLow ? 0.55 : 1)}
                                        zIndex={zIndex[index]}
                                        disabled={completed}
                                    >
                                        <Drag
                                            className={cn(
                                                completed && "Disabled",
                                                checkPlaying &&
                                                playingDrop === solutions.indexOf(index) &&
                                                "CheckPlayingBar",
                                                isBarPlay && playingDrop === randomOrder[index] && "PlayingBar"
                                            )}
                                            style={{
                                                transform: `scale(${scale})`,
                                                transformOrigin: "top left",
                                            }}
                                        >
                                            <Box
                                                color={data.color}
                                                isChristmas={isChristmas}
                                                index={index}
                                                box={box}
                                                width={widths[randomOrder[index]]}
                                                onPlayBar={this.onPlayBar}
                                                rating={
                                                    (incorrectBars &&
                                                        (incorrectBars.indexOf(index) !== -1 ? "Incorrect" : "Correct")
                                                    ) || undefined
                                                }
                                            />
                                        </Drag>
                                    </Draggable>
                                );
                            })}
                            {drops.map((drop, index) => {
                                return (
                                    <Drop
                                        scale={scale}
                                        key={index}
                                        className={cn(
                                            dropTarget === index && "Active",
                                            playingDrop === index &&
                                            !isBarPlay &&
                                            (task.data.follow === undefined || task.data.follow) &&
                                            "Playing",
                                            isChristmas && "Christmas"
                                        )}
                                        style={{
                                            top: drop.y,
                                            left: drop.x,
                                            width: drop.width,
                                            height: 80 * scale,
                                        }}
                                    ></Drop>
                                );
                            })}
                        </Playground>
                        <PlayBtn
                            isEaster={isEaster}
                            isLow={isLow}
                            className={cn("button", "button--play")}
                            onClick={() => this.play()}
                            disabled={checkPlaying}
                        >
                            {playing ? <Icon i="stop" /> : <Icon i="play" />}
                            <Label>
                                <FormattedMessage {...messages.play} />
                            </Label>
                        </PlayBtn>
                        {submitable && (
                            <CheckPlayBtn
                                isEaster={isEaster}
                                isLow={isLow}
                                className={cn("button", "button--check")}
                                onClick={() => this.play(true)}
                                disabled={playing}
                            >
                                {checkPlaying ? <Icon i="stop" /> : <Icon i="play" />}
                                <Label>
                                    <FormattedMessage {...messages.check} />
                                </Label>
                            </CheckPlayBtn>
                        )}
                        {submitable && (!incorrectBars || incorrectBars.length === 0) && !completed && (
                            <SubmitButton
                                isEaster={isEaster}
                                isLow={isLow}
                                style={{ top: isLow ? 30 : 6, bottom: "auto" }}
                                className={cn("SubmitActive", "button")}
                                onClick={this.submit}
                            >
                                <FormattedMessage {...messages.submit} />
                            </SubmitButton>
                        )}
                        {completed && (
                            <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="completed" handleHomeworkNext={handleHomeworkNext} />
                        )}
                        {incorrectBars && incorrectBars.length > 0 && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="incorrect" />}
                    </Wrapper>
                )}
            </div>
        );
    }
}

export default RhytSorting;
